import { useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import {
  type Receivable,
  type CreateReceivablePayload,
  type UpdateReceivablePayload,
  type GetReceivablesParams,
  type PaginatedReceivablesResponse,
  type ReceivableDocument,
  type CreateReceivableDocumentPayload,
  type UpdateReceivableDocumentPayload,
  type ReceivableTax,
  type CreateReceivableTaxPayload,
  type UpdateReceivableTaxPayload,
  type ReceivableTaxDocument,
  type CreateReceivableTaxDocumentPayload,
} from '../utils/types';
import { receivableApi } from '../api';
import { receivableKeys } from '../utils/receivable.keys';
import { AxiosError } from 'axios';

// Query de listagem de receivables
export function useReceivables(
  filters?: GetReceivablesParams,
  options?: Omit<UseQueryOptions<PaginatedReceivablesResponse, Error>, 'queryKey' | 'queryFn'>
) {
  const query = useQuery({
    queryKey: receivableKeys.list(filters),
    queryFn: ({ signal }) => receivableApi.list(filters, signal),
    staleTime: 60_000,
    ...(options as Omit<UseQueryOptions<PaginatedReceivablesResponse, Error>, 'queryKey' | 'queryFn'>),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: 'Erro ao buscar recebíveis',
      description: query.error.message,
      variant: 'destructive',
    });
  }, [query.error]);

  return {
    receivables: query.data?.data ?? [],
    totalPages: query.data?.totalPage,
    totalItems: query.data?.totalItems ?? 0,
    currentPage: query.data?.page ?? filters?.page ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

// Query de detalhe
export function useReceivable(
  id?: string,
  options?: Omit<UseQueryOptions<Receivable, Error>, 'queryKey' | 'queryFn'>
) {
  const queryKey = id ? receivableKeys.detail(id) : [...receivableKeys.details(), ''];
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      if (!id) throw new Error('ID do recebível não informado');
      return receivableApi.get(id, signal);
    },
    enabled: Boolean(id),
    ...(options as Omit<UseQueryOptions<Receivable, Error>, 'queryKey' | 'queryFn'>),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: 'Erro ao carregar recebível',
      description: query.error.message,
      variant: 'destructive',
    });
  }, [query.error]);

  return {
    receivable: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

// Mutação de criação
export function useCreateReceivable(
  options?: UseMutationOptions<Receivable, Error, CreateReceivablePayload>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'create'],
    mutationFn: receivableApi.create,
    ...mutationOptions,
    onSuccess: (receivable, variables, onMutateResult, context) => {
      toast({
        title: 'Recebível criado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.all });
      providedOnSuccess?.(receivable, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao criar recebível',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  return {
    createReceivable: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

// Mutação de atualização
export function useUpdateReceivable(
  options?: UseMutationOptions<Receivable, Error, { id: string; data: UpdateReceivablePayload }>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'update'],
    mutationFn: ({ id, data }: { id: string; data: UpdateReceivablePayload }) => receivableApi.update(id, data),
    ...mutationOptions,
    onSuccess: (receivable, variables, onMutateResult, context) => {
      toast({
        title: 'Recebível atualizado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: receivableKeys.all });
      providedOnSuccess?.(receivable, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      const err = error as AxiosError<{ message: string }>;

      toast({
        title: 'Erro ao atualizar recebível',
        description: err?.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  const updateReceivable = (id: string, data: UpdateReceivablePayload) => mutation.mutateAsync({ id, data });
  return {
    updateReceivable,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

// Mutação de remoção
export function useDeleteReceivable(
  options?: UseMutationOptions<{ id: string; deletedAt: string }, Error, string>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'delete'],
    mutationFn: receivableApi.remove,
    ...mutationOptions,
    onSuccess: (response, variables, onMutateResult, context) => {
      toast({
        title: 'Recebível excluído com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.all });
      providedOnSuccess?.(response, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao excluir recebível',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  const deleteReceivable = (receivableId: string) => mutation.mutateAsync(receivableId);
  return {
    deleteReceivable,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useReceivableDocuments(
  receivableId?: string,
  options?: Omit<UseQueryOptions<ReceivableDocument[], Error>, 'queryKey' | 'queryFn'>
) {
  const query = useQuery({
    queryKey: receivableId ? receivableKeys.documents(receivableId) : [...receivableKeys.all, 'documents', ''],
    queryFn: ({ signal }) => {
      if (!receivableId) throw new Error('ID do recebível não informado');
      return receivableApi.documents.list(receivableId, signal);
    },
    enabled: Boolean(receivableId),
    staleTime: 60_000,
    ...(options as Omit<UseQueryOptions<ReceivableDocument[], Error>, 'queryKey' | 'queryFn'>),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: 'Erro ao carregar documentos',
      description: query.error.message,
      variant: 'destructive',
    });
  }, [query.error]);

  return {
    documents: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreateReceivableDocument(
  options?: UseMutationOptions<ReceivableDocument, Error, CreateReceivableDocumentPayload>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'documents', 'create'],
    mutationFn: receivableApi.documents.create,
    ...mutationOptions,
    onSuccess: (document, variables, onMutateResult, context) => {
      toast({
        title: 'Documento anexado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.documents(variables.receivableId) });
      queryClient.invalidateQueries({ queryKey: ['receivables', 'detail', variables.receivableId] });
      providedOnSuccess?.(document, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao anexar documento',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  return {
    createDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

type UpdateReceivableDocumentVariables = UpdateReceivableDocumentPayload & { receivableId: string };

export function useUpdateReceivableDocument(
  options?: UseMutationOptions<ReceivableDocument, Error, UpdateReceivableDocumentVariables>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'documents', 'update'],
    mutationFn: (variables: UpdateReceivableDocumentVariables) => receivableApi.documents.update(variables),
    ...mutationOptions,
    onSuccess: (document, variables, onMutateResult, context) => {
      toast({
        title: 'Documento atualizado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.documents(variables.receivableId) });
      queryClient.invalidateQueries({ queryKey: ['receivables', 'detail', variables.receivableId] });
      providedOnSuccess?.(document, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao atualizar documento',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  return {
    updateDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

interface DeleteReceivableDocumentVariables {
  id: string;
  receivableId: string;
}

export function useDeleteReceivableDocument(
  options?: UseMutationOptions<{ id: string; deletedAt?: string }, Error, DeleteReceivableDocumentVariables>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'documents', 'delete'],
    mutationFn: ({ receivableId, id }: DeleteReceivableDocumentVariables) => receivableApi.documents.remove(receivableId, id),
    ...mutationOptions,
    onSuccess: (response, variables, onMutateResult, context) => {
      toast({
        title: 'Documento removido com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.documents(variables.receivableId) });
      queryClient.invalidateQueries({ queryKey: ['receivables', 'detail', variables.receivableId] });
      providedOnSuccess?.(response, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao remover documento',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  return {
    deleteDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useReceivableTaxes(
  receivableId?: string,
  search?: string,
  options?: Omit<UseQueryOptions<ReceivableTax[], Error>, 'queryKey' | 'queryFn'>
) {
  const query = useQuery({
    queryKey: receivableId ? receivableKeys.taxes(receivableId, search) : [...receivableKeys.all, 'taxes', '', search],
    queryFn: ({ signal }) => {
      if (!receivableId) throw new Error('ID do recebível não informado');
      return receivableApi.taxes.list(receivableId, search, signal);
    },
    enabled: Boolean(receivableId),
    staleTime: 60_000,
    ...(options as Omit<UseQueryOptions<ReceivableTax[], Error>, 'queryKey' | 'queryFn'>),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: 'Erro ao carregar taxas',
      description: query.error.message,
      variant: 'destructive',
    });
  }, [query.error]);

  return {
    taxes: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreateReceivableTax(
  options?: UseMutationOptions<ReceivableTax, Error, CreateReceivableTaxPayload>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'taxes', 'create'],
    mutationFn: receivableApi.taxes.create,
    ...mutationOptions,
    onSuccess: (tax, variables, onMutateResult, context) => {
      toast({
        title: 'Taxa registrada com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.taxes(variables.receivableId) });
      queryClient.invalidateQueries({ queryKey: ['receivables', 'detail', variables.receivableId] });
      providedOnSuccess?.(tax, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao registrar taxa',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  return {
    createTax: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

type UpdateReceivableTaxVariables = UpdateReceivableTaxPayload & { receivableId: string };

export function useUpdateReceivableTax(
  options?: UseMutationOptions<ReceivableTax, Error, UpdateReceivableTaxVariables>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'taxes', 'update'],
    mutationFn: (variables: UpdateReceivableTaxVariables) => receivableApi.taxes.update(variables),
    ...mutationOptions,
    onSuccess: (tax, variables, onMutateResult, context) => {
      toast({
        title: 'Taxa atualizada com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.taxes(variables.receivableId) });
      queryClient.invalidateQueries({ queryKey: ['receivables', 'detail', variables.receivableId] });
      providedOnSuccess?.(tax, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao atualizar taxa',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  return {
    updateTax: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

interface DeleteReceivableTaxVariables {
  id: string;
  receivableId: string;
}

export function useDeleteReceivableTax(
  options?: UseMutationOptions<{ id: string; deletedAt?: string }, Error, DeleteReceivableTaxVariables>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'taxes', 'delete'],
    mutationFn: ({ id }: DeleteReceivableTaxVariables) => receivableApi.taxes.remove(id),
    ...mutationOptions,
    onSuccess: (response, variables, onMutateResult, context) => {
      toast({
        title: 'Taxa removida com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.taxes(variables.receivableId) });
      queryClient.invalidateQueries({ queryKey: ['receivables', 'detail', variables.receivableId] });
      providedOnSuccess?.(response, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao remover taxa',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  return {
    deleteTax: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useReceivableTaxDocuments(
  taxId?: string,
  options?: Omit<UseQueryOptions<ReceivableTaxDocument[], Error>, 'queryKey' | 'queryFn'>,
) {
  const query = useQuery({
    queryKey: taxId ? receivableKeys.taxDocuments(taxId) : [...receivableKeys.all, 'taxes', 'documents', ''],
    queryFn: ({ signal }) => {
      if (!taxId) throw new Error('ID da taxa não informado');
      return receivableApi.taxes.documents.list(taxId, signal);
    },
    enabled: Boolean(taxId),
    staleTime: 60_000,
    ...(options as Omit<UseQueryOptions<ReceivableTaxDocument[], Error>, 'queryKey' | 'queryFn'>),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: 'Erro ao carregar documentos da taxa',
      description: query.error.message,
      variant: 'destructive',
    });
  }, [query.error]);

  return {
    documents: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

type CreateTaxDocumentVariables = CreateReceivableTaxDocumentPayload;

export function useCreateReceivableTaxDocument(
  options?: UseMutationOptions<ReceivableTaxDocument, Error, CreateTaxDocumentVariables>,
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'taxes', 'documents', 'create'],
    mutationFn: receivableApi.taxes.documents.create,
    ...mutationOptions,
    onSuccess: (document, variables, onMutateResult, context) => {
      toast({
        title: 'Documento anexado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: receivableKeys.taxDocuments(variables.receivableTaxId) });
      queryClient.invalidateQueries({ queryKey: receivableKeys.all });
      providedOnSuccess?.(document, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao anexar documento',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  return {
    createTaxDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}


interface DeleteTaxDocumentVariables {
  id: string;
  receivableId: string;
}

export function useDeleteReceivableTaxDocument(
  options?: UseMutationOptions<{ id: string; deletedAt?: string }, Error, DeleteTaxDocumentVariables>,
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['receivables', 'taxes', 'documents', 'delete'],
    mutationFn: ({ id, receivableId }: DeleteTaxDocumentVariables) => receivableApi.taxes.documents.remove({ id, receivableId }),
    ...mutationOptions,
    onSuccess: (response, variables, onMutateResult, context) => {
      toast({
        title: 'Documento removido com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['receivables', 'taxes', 'documents', variables.receivableId] });
      queryClient.invalidateQueries({ queryKey: receivableKeys.all });
      providedOnSuccess?.(response, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao remover documento',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  return {
    deleteTaxDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
